import { createContext, useContext, useMemo, useState, type ReactNode } from "react";
import {
  mockBranchEmployees,
  phonePresetMap,
  defaultRole,
} from "@/mocks/branch/employee";
import type { PermissionKey, PermissionSet } from "@/config/permissions";

export type Role = "cashier" | "shift_supervisor" | "operations" | "branch_manager" | "owner";

export type BranchPermissions = PermissionSet;

export interface BranchEmployee {
  name: string;
  phone: string;
  role: Role;
  branchName: string;
  storeName: string;
  permissions: BranchPermissions;
  /** true عندما تكون الجلسة «معاينة تصميم» وليست دخولًا حقيقيًا */
  isPreview: boolean;
}

interface BranchContextValue {
  employee: BranchEmployee | null;
  isAuthenticated: boolean;
  /** هل الجلسة الحالية وضع معاينة تصميم؟ */
  isPreview: boolean;
  /** دخول حقيقي: النظام يتعرّف على الموظف من رقم الجوال ويسترجع مسمّاه وصلاحياته المحفوظة */
  login: (phone: string) => BranchEmployee;
  /** فتح اللوحة لأغراض معاينة التصميم حسب المسمى والصلاحيات المختارة */
  enterPreview: (role: Role, permissions: PermissionSet) => BranchEmployee;
  logout: () => void;
  updatePhone: (phone: string) => void;
  /** هل يملك الموظف صلاحية تشغيلية معيّنة؟ */
  can: (key: PermissionKey) => boolean;
}

const STORAGE_KEY = "yc_branch_session";

const BranchContext = createContext<BranchContextValue | undefined>(undefined);

function sanitizePermissions(raw: unknown): PermissionSet {
  const source = (raw ?? {}) as Partial<Record<PermissionKey, boolean>>;
  return {
    receiveOrders: Boolean(source.receiveOrders),
    updateOrderStatus: Boolean(source.updateOrderStatus),
    pos: Boolean(source.pos),
    productAvailability: Boolean(source.productAvailability),
    editQuantities: Boolean(source.editQuantities),
    customerRatings: Boolean(source.customerRatings),
    workingHours: Boolean(source.workingHours),
    pickupPolicy: Boolean(source.pickupPolicy),
    emergencyClosure: Boolean(source.emergencyClosure),
    reports: Boolean(source.reports),
  };
}

function readSession(): BranchEmployee | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<BranchEmployee>;
    if (!parsed || !parsed.role || !parsed.permissions) return null;
    return {
      name: parsed.name ?? "",
      phone: parsed.phone ?? "",
      role: parsed.role,
      branchName: parsed.branchName ?? "",
      storeName: parsed.storeName ?? "",
      permissions: sanitizePermissions(parsed.permissions),
      isPreview: Boolean(parsed.isPreview),
    };
  } catch {
    return null;
  }
}

function persist(employee: BranchEmployee | null) {
  try {
    if (employee) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(employee));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // ignore storage failures
  }
}

function buildEmployee(role: Role, phone: string, permissions: PermissionSet, isPreview: boolean): BranchEmployee {
  const base = mockBranchEmployees[role] ?? mockBranchEmployees[defaultRole];
  return {
    name: base.name,
    phone: phone || base.phone,
    role,
    branchName: base.branchName,
    storeName: base.storeName,
    permissions,
    isPreview,
  };
}

export function BranchProvider({ children }: { children: ReactNode }) {
  const [employee, setEmployee] = useState<BranchEmployee | null>(() => readSession());

  const login = (phone: string) => {
    const role = phonePresetMap[phone] ?? defaultRole;
    // النظام يسترجع الدور والصلاحيات المحفوظة لدى صاحب المحل من رقم الجوال
    const saved = mockBranchEmployees[role];
    const next = buildEmployee(role, phone, { ...saved.permissions }, false);
    setEmployee(next);
    persist(next);
    return next;
  };

  const enterPreview = (role: Role, permissions: PermissionSet) => {
    const basePhone = (mockBranchEmployees[role] ?? mockBranchEmployees[defaultRole]).phone;
    const next = buildEmployee(role, basePhone, { ...permissions }, true);
    setEmployee(next);
    persist(next);
    return next;
  };

  const logout = () => {
    setEmployee(null);
    persist(null);
  };

  const updatePhone = (phone: string) => {
    setEmployee((prev) => {
      if (!prev) return prev;
      const next = { ...prev, phone };
      persist(next);
      return next;
    });
  };

  const value = useMemo<BranchContextValue>(() => {
    const can = (key: PermissionKey) => Boolean(employee?.permissions?.[key]);
    return {
      employee,
      isAuthenticated: Boolean(employee),
      isPreview: Boolean(employee?.isPreview),
      login,
      enterPreview,
      logout,
      updatePhone,
      can,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employee]);

  return <BranchContext.Provider value={value}>{children}</BranchContext.Provider>;
}

export function useBranch(): BranchContextValue {
  const ctx = useContext(BranchContext);
  if (!ctx) {
    throw new Error("useBranch must be used within a BranchProvider");
  }
  return ctx;
}